/**
 * Which account the AO stores currently hold data for.
 *
 * Both stores persist to a single localStorage key, so data is stamped with the account
 * and reset when the active account changes (otherwise one account's criteria could be
 * pushed to another).
 */
import { getCurrentAccountId } from 'app/shared/api/accountContext';

/** Callbacks the stores register, run when the active account changes. */
const resetters = new Set<(accountId: string) => void>();

export function onAccountChanged(reset: (accountId: string) => void): () => void {
  resetters.add(reset);
  return () => resetters.delete(reset);
}

/**
 * Tell the AO stores the active account changed. Called by `useSwitchAccount`.
 */
export function notifyAccountChanged(accountId: string): void {
  resetters.forEach(reset => reset(accountId));
}

/**
 * Compare a store's stamp with the active account.
 *
 * - `'unknown'`: account not resolved yet (normal on first paint), never reset
 * - `'adopt'`: data has no stamp, attribute it to the current account
 * - `'same'`: nothing to do
 * - `'changed'`: data belongs to another account, reset
 */
export type AccountVerdict = 'unknown' | 'adopt' | 'same' | 'changed';

export function compareAccount(stamped: string | null | undefined): AccountVerdict {
  const current = getCurrentAccountId();
  if (!current) return 'unknown';
  if (!stamped) return 'adopt';
  return stamped === current ? 'same' : 'changed';
}

export { getCurrentAccountId };
